import React from 'react';
import { Box, Typography } from '@mui/material';
import { Folder as FolderIcon } from '@mui/icons-material';
import { FileSelectionTreeProps } from '../types';

/**
 * Hierarchical file tree with checkboxes for file selection
 * Sorts folders first, then files (both alphabetically)
 * Indents by depth for visual hierarchy
 */
export const FileSelectionTree: React.FC<FileSelectionTreeProps> = ({
  files,
  selectedFiles,
  onToggleFile,
  maxHeight = 400,
}) => {
  const sortedFiles = [...files].sort((a, b) => {
    // Sort: folders first, then files, both alphabetically
    const aIsFolder = a.type === 'folder' || a.name.endsWith('/');
    const bIsFolder = b.type === 'folder' || b.name.endsWith('/');

    if (aIsFolder && !bIsFolder) return -1;
    if (!aIsFolder && bIsFolder) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1
      }}
    >
      {sortedFiles.map((file) => {
        const isFolder = file.type === 'folder' || file.name.endsWith('/');
        const depth = (file.path.match(/\//g) || []).length - 1;
        const paddingLeft = Math.max(0, depth) * 20;

        return (
          <Box
            key={file.id}
            display="flex"
            alignItems="center"
            gap={1}
            py={0.75}
            px={1}
            sx={{
              paddingLeft: `${paddingLeft + 8}px`,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' }
            }}
          >
            {!isFolder && (
              <input
                type="checkbox"
                checked={selectedFiles.includes(file.id)}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  onToggleFile(file.id);
                }}
              />
            )}

            <Box display="flex" alignItems="center" gap={0.5}>
              {isFolder ? (
                <FolderIcon fontSize="small" color="action" />
              ) : (
                <Typography component="span" fontSize="small">📄</Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontWeight: isFolder ? 500 : 400,
                  color: isFolder ? 'text.primary' : 'text.secondary'
                }}
              >
                {file.name}
              </Typography>
            </Box>

            {!isFolder && (
              <Typography variant="caption" color="text.secondary">
                {file.size > 1024 * 1024
                  ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
                  : `${(file.size / 1024).toFixed(1)} KB`
                }
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
